import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Irvine", cities: "Woodbridge, Northwood, Turtle Rock, University Park, Westpark" },
    { zone: "Newport Beach", cities: "Newport Beach, Costa Mesa, Corona del Mar, Balboa Island" },
    { zone: "Mission Viejo", cities: "Mission Viejo, Lake Forest, Laguna Niguel, Aliso Viejo" },
    { zone: "North OC", cities: "Anaheim, Fullerton, Orange, Brea, Placentia, Yorba Linda" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Orange</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo el Condado de Orange y sus comunidades.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Irvine</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const IrvineSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "irvine", name: "Irvine", type: "city", regionSlug: "orange-county" }}>
      <SEO
        title="Limpieza del Hogar en Irvine, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Irvine, Newport Beach, Mission Viejo, Costa Mesa y más. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar irvine, servicio de limpieza irvine ca, limpieza profunda irvine, empleada doméstica irvine, maid service orange county español, limpieza de casas newport beach, limpieza mission viejo costa mesa, limpieza condado de orange"
        url="https://uplevelcleaning.com/es/irvine"
        locationName="Irvine"
        regionSlug="orange-county"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Irvine", url: "/es/irvine" }
        ]}
        reviews={[
          { author: "Gabriela M.", rating: 5, reviewBody: "Vivo en Irvine y buscaba un servicio de limpieza profesional y de confianza. ¡Uplevel Cleaning superó todas mis expectativas! Mi casa quedó perfecta.", datePublished: "2026-03-25" },
          { author: "Fernando R.", rating: 5, reviewBody: "Contraté la limpieza quincenal para mi casa en Newport Beach. El equipo es puntual, amable y muy detallista. ¡Los recomiendo al 100%!", datePublished: "2026-02-19" },
          { author: "Lucia V.", rating: 5, reviewBody: "Llevaba tiempo buscando un servicio confiable en el Condado de Orange. Encontré Uplevel Cleaning y no podría estar más contenta. ¡Excelente trabajo!", datePublished: "2026-01-12" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Irvine" regionSlug="orange-county" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en Irvine",
                "Condado de Orange Completo",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Irvine" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="irvine" />
          <Guarantee />
          <SpanishFAQ cityName="Irvine" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Irvine" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default IrvineSpanishPage;
